import { toast } from 'sonner';
import { messages, type MessageKey } from '../i18n';
import { getLocale, t, type Vars } from './i18n';

/**
 * Human-readable, translated error messages. The server sends a stable `key` (+ params) next
 * to its English message; validation errors list the invalid fields, which we name the way
 * the forms do ("Name", "Webhook URL") instead of showing raw paths like "name".
 */

export interface FieldIssue {
  path: string | Array<PropertyKey>;
  code: string;
  message?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  format?: string;
  origin?: string;
  input?: unknown;
}

const has = (key: string): key is MessageKey => key in messages;

/** Label for a field path such as "destinations.0.url" → "Webhook URL". */
export function fieldLabel(path: string | Array<PropertyKey>): string {
  const parts = (typeof path === 'string' ? path.split(/[./]/) : path.map(String)).filter(
    (p) => p && !/^\d+$/.test(p),
  );
  // Most specific first: "destinations.url", then "url"
  for (let i = 0; i < parts.length; i++) {
    const key = `field.${parts.slice(i).join('.')}`;
    if (has(key)) return t(key);
  }
  return parts.join(' → ') || '—';
}

const num = (v: number | bigint | undefined) => (v === undefined ? undefined : Number(v));

/** One invalid field as a sentence: "Fill in «Name»". */
export function issueMessage(issue: FieldIssue): string {
  const field = fieldLabel(issue.path);
  const min = num(issue.minimum);
  const max = num(issue.maximum);
  const origin = issue.origin;
  const missing =
    issue.code === 'invalid_type' &&
    ('input' in issue ? issue.input === undefined : /received undefined/.test(issue.message ?? ''));
  if (missing) return t('err.field.required', { field });
  switch (issue.code) {
    case 'too_small':
      if (origin === 'array' || origin === 'set') return t('err.field.choose', { field });
      if (min !== undefined && min <= 1 && origin !== 'number')
        return t('err.field.required', { field });
      if (min !== undefined)
        return t(origin === 'number' ? 'err.field.min' : 'err.field.minLength', { field, n: min });
      return t('err.field.required', { field });
    case 'too_big':
      if (max !== undefined)
        return t(origin === 'number' ? 'err.field.max' : 'err.field.maxLength', { field, n: max });
      break;
    case 'invalid_format':
      if (issue.format === 'email') return t('err.field.email');
      if (issue.format === 'url') return t('err.field.url', { field });
      break;
  }
  return t('err.field.invalid', { field });
}

/** Up to three field problems, one per line. */
export function issuesMessage(issues: FieldIssue[]): string {
  const lines = [...new Set(issues.map(issueMessage))];
  return lines.slice(0, 3).join('\n');
}

interface ErrorLike {
  message: string;
  code?: string;
  key?: string;
  params?: Vars;
  details?: unknown;
}

const isIssueList = (v: unknown): v is FieldIssue[] =>
  Array.isArray(v) && v.length > 0 && v.every((i) => i && typeof i === 'object' && 'code' in i);

/** Translated text for an API error (falls back to the server's English text). */
export function describeError(err: ErrorLike): string {
  if (isIssueList(err.details) && (err.key === 'validation' || err.code === 'validation_error'))
    return issuesMessage(err.details);
  if (err.key && has(`err.${err.key}`)) {
    const params = { ...err.params };
    // Role names are translated like in the team settings
    if (typeof params.role === 'string' && has(`role.${params.role}`))
      params.role = t(`role.${params.role}` as MessageKey);
    return t(`err.${err.key}` as MessageKey, params);
  }
  if (getLocale() !== 'en' && err.code && has(`err.code.${err.code}`))
    return t(`err.code.${err.code}` as MessageKey);
  return err.message;
}

/** The plan is the problem: offer the way out right in the notification. */
export const isPlanError = (code: string | undefined) =>
  code === 'plan_limit' || code === 'quota_exceeded';

export function openBilling() {
  window.history.pushState({}, '', '/app/settings/billing');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Show an error toast; plan limits get a "See plans" button. */
export function toastError(err: unknown) {
  const code = (err as { code?: string } | null)?.code;
  const text = describeUnknown(err);
  toast.error(
    text,
    isPlanError(code) ? { action: { label: t('err.seePlans'), onClick: openBilling } } : undefined,
  );
}

export function describeUnknown(err: unknown): string {
  if (err && typeof err === 'object' && 'status' in err && 'message' in err)
    return describeError(err as ErrorLike);
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) return t('err.network');
  if (err instanceof Error) return err.message;
  return String(err);
}
